import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Send, Mail, X } from "lucide-react";
import client, { assetUrl } from "@/lib/glossyApi";
import { Header } from "./shared";

export default function Newsletter() {
  const [nl, setNl] = useState(null);
  const [busy, setBusy] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [preview, setPreview] = useState(null);

  const loadPreview = () => client.get(`/admin/newsletter/preview?origin=${encodeURIComponent(window.location.origin)}`).then((r) => setPreview(r.data)).catch(() => {});
  const load = () => client.get("/admin/newsletter").then((r) => { setNl(r.data); loadPreview(); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const patch = async (body) => {
    try { const r = await client.patch("/admin/newsletter", body); setNl(r.data); loadPreview(); }
    catch { toast.error("Could not save."); }
  };
  const removeMember = (id) => patch({ member_ids: nl.members.filter((m) => m.id !== id).map((m) => m.id) });
  const refresh = async () => {
    setBusy("refresh");
    try { const r = await client.post("/admin/newsletter/refresh"); setNl(r.data); loadPreview(); toast.success("Fresh rotation picked."); }
    catch { toast.error("Could not refresh."); }
    setBusy("");
  };
  const sendTest = async () => {
    setBusy("test");
    try { const r = await client.post("/admin/newsletter/test", { origin: window.location.origin, to: testEmail.trim() }); toast.success(`Test sent to ${r.data.sent_to}.`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send test."); }
    setBusy("");
  };
  const sendReal = async () => {
    if (!window.confirm(`Send this newsletter to all ${nl?.subscriber_count ?? 0} subscribers?`)) return;
    setBusy("send");
    try { const r = await client.post("/admin/newsletter/send", { origin: window.location.origin }); toast.success(`Sent to ${r.data.sent} subscribers.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send."); }
    setBusy("");
  };

  if (!nl) return <div className="p-6 sm:p-10"><p className="font-sans text-sm text-ink/40">Composing this edition's newsletter…</p></div>;

  return (
    <div data-testid="newsletter-desk">
      <Header kicker="The fortnightly edit" title="Newsletter">
        <p className="overline text-ink/50 flex items-center gap-2"><Mail size={14} /> {nl.subscriber_count} subscriber{nl.subscriber_count === 1 ? "" : "s"} · {nl.status === "sent" ? `Sent ${String(nl.sent_at).slice(0, 10)} to ${nl.sent_count}` : "Draft — nothing sends until you say so"}</p>
      </Header>
      <div className="p-6 sm:p-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="Send a test to… (any email)"
            className="border border-ink/20 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-ink bg-white w-72" data-testid="newsletter-test-email" />
          <button onClick={sendTest} disabled={!!busy} className="overline px-4 py-2.5 border border-ink/30 hover:border-ink transition-colors disabled:opacity-60" data-testid="newsletter-test-btn">
            {busy === "test" ? "Sending…" : "Send test"}
          </button>
        </div>
        <div className="flex gap-2">
          <button onClick={sendReal} disabled={!!busy || nl.status === "sent"} className="overline px-4 py-2.5 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="newsletter-send-btn">
            <Send size={13} /> {nl.status === "sent" ? "Sent" : "Send newsletter"}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div>
          <p className="overline text-ink/50 mb-1">Subject line</p>
          <input defaultValue={nl.subject} onBlur={(e) => e.target.value !== nl.subject && patch({ subject: e.target.value })}
            className="w-full border border-ink/20 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-ink bg-white" data-testid="newsletter-subject" />
        </div>
        <div>
          <p className="overline text-ink/50 mb-1">Intro (your editor's note)</p>
          <textarea defaultValue={nl.intro} rows={2} onBlur={(e) => e.target.value !== nl.intro && patch({ intro: e.target.value })}
            className="w-full border border-ink/20 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-ink bg-white" data-testid="newsletter-intro" />
        </div>
      </div>
      <div className="flex items-center justify-between mb-2">
        <p className="overline text-ink/50">Featured this edition ({nl.members.length}) — members without a magazine drop this fortnight, rotated fairly</p>
        <button onClick={refresh} disabled={!!busy} className="overline px-3 py-1.5 border border-ink/25 hover:border-ink transition-colors flex items-center gap-1.5 disabled:opacity-60" data-testid="newsletter-refresh-btn">
          {busy === "refresh" ? <RefreshCw size={12} className="animate-spin" /> : <RefreshCw size={12} />} Fresh picks
        </button>
      </div>
      <div className="bg-white border border-ink/15">
        {nl.members.length === 0 && <p className="font-sans text-sm text-ink/40 px-5 py-6">No members to feature — everyone published is already in this edition's magazine drops.</p>}
        {nl.members.map((m) => (
          <div key={m.id} className="flex items-center gap-4 px-5 py-3 border-b border-ink/10 last:border-0" data-testid={`newsletter-member-${m.id}`}>
            {assetUrl(m.image) && <img src={assetUrl(m.image)} alt="" className="w-10 h-10 object-cover shrink-0" />}
            <div className="min-w-0 flex-1">
              <p className="font-serif text-base truncate">{m.business_name}</p>
              <p className="font-sans text-xs text-ink/50 truncate">{[m.category, m.area].filter(Boolean).join(" · ")}</p>
            </div>
            <button onClick={() => removeMember(m.id)} className="p-1.5 hover:text-red-500 transition-colors shrink-0" aria-label="Remove" data-testid={`newsletter-remove-${m.id}`}><X size={15} /></button>
          </div>
        ))}
      </div>

      {preview && (
        <div className="mt-10" data-testid="newsletter-preview">
          <div className="flex items-baseline justify-between mb-2">
            <p className="overline text-ink/50">Exactly what lands in their inbox</p>
            {preview.fallback_members && <p className="font-mono text-[11px] text-ink/40">Preview shows your published members — the real send uses the rotation above</p>}
          </div>
          <div className="border border-ink/15 bg-white">
            <iframe title="Newsletter preview" srcDoc={preview.html} className="w-full" style={{ height: 720, border: 0 }} data-testid="newsletter-preview-frame" />
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
